import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {Menu, X, Globe, ChevronDown, ShoppingCart, Heart, User, LogIn, LogOut, Package} from 'lucide-react'
import ThemeToggle from './ThemeToggle'
import LoginModal from './LoginModal'
import { useCart } from '../contexts/CartContext'

import { lumi } from '../lib/lumi'
import { getFavoritesCountFromCache } from '../utils/favoritesCache'
import { fetchFavoritesCountForUser } from '../utils/favoritesService'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { itemCount } = useCart()
  
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [user, setUser] = useState(lumi.auth.user)
  const [favoriteCount, setFavoriteCount] = useState(() => getFavoritesCountFromCache())

  // 🔐 全局防重复机制（跨组件实例）
  const isUserSyncing = (email: string): boolean => {
    const syncKey = `user_syncing_${email}`
    const syncData = localStorage.getItem(syncKey)
    
    if (!syncData) return false
    
    const { timestamp } = JSON.parse(syncData)
    const now = Date.now()
    
    // 如果同步标记超过 30 秒，认为已过期（防止死锁）
    if (now - timestamp > 30000) {
      localStorage.removeItem(syncKey)
      return false
    }
    
    return true
  }

  const markUserSyncing = (email: string, syncing: boolean) => {
    const syncKey = `user_syncing_${email}`
    if (syncing) {
      localStorage.setItem(syncKey, JSON.stringify({ timestamp: Date.now() }))
    } else {
      localStorage.removeItem(syncKey)
    }
  }

  const isEmailSent = (email: string): boolean => {
    const sentKey = `email_sent_${email}`
    return localStorage.getItem(sentKey) === 'true'
  }

  const markEmailSent = (email: string) => {
    const sentKey = `email_sent_${email}`
    localStorage.setItem(sentKey, 'true')
  }

  // Sync User to MongoDB
  const syncUserToDB = async (userData: any) => {
    if (!userData) {
      console.log('[syncUserToDB] No user data provided');
      return;
    }
    
    // 🔒 全局防并发检查：如果正在同步此用户，直接返回
    if (isUserSyncing(userData.email)) {
      console.log('[syncUserToDB] ⏸️ User is being synced by another process:', userData.email, '- aborting');
      return;
    }
    
    // 标记开始同步（全局）
    markUserSyncing(userData.email, true);
    console.log('[syncUserToDB] ✅ Starting sync for user:', userData.email);
    console.log('[syncUserToDB] 📧 User email:', userData.email);
    console.log('[syncUserToDB] 👤 User name:', userData.name);
    console.log('[syncUserToDB] 🆔 User ID:', userData.id);
    
    try {
      // Check if user exists (query by email, not userId)
      console.log('[syncUserToDB] 🔍 Checking if user exists in customers collection...');
      const { list: existingUsers } = await lumi.entities.customers.list({
        filter: { email: userData.email },
        limit: 10  // 限制查询数量，提高性能
      });

      console.log('[syncUserToDB] 📊 Query completed - Found users:', existingUsers?.length || 0);
      
      // 显示所有找到的用户记录
      if (existingUsers && existingUsers.length > 0) {
        console.log('[syncUserToDB] 📋 Existing user records:');
        existingUsers.forEach((user: any, index: number) => {
          console.log(`  [${index + 1}] Email: ${user.email}, Created: ${user.createdAt}`);
        });
      }
      
      if (existingUsers.length === 0) {
        // 🔐 二次检查：在创建前再次确认用户不存在（防止极端竞态条件）
        console.log('[syncUserToDB] 🔐 Performing double-check before creation...');
        const { list: doubleCheck } = await lumi.entities.customers.list({
          filter: { email: userData.email },
          limit: 1
        });
        
        if (doubleCheck.length > 0) {
          console.log('[syncUserToDB] ⚠️ User was created by another process during sync - aborting');
          return;
        }
        
        // User does not exist, create new
        console.log('[syncUserToDB] 🆕 User NOT found in customers. Creating new record...');
        const newCustomer = {
          userId: userData.id,
          email: userData.email,
          userName: userData.name || userData.email.split('@')[0],
          memberLevel: 'LV1',
          status: 'active',
          totalSpent: 0,
          points: 0,
          tags: [],
          preferences: {},
          cart: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        console.log('[syncUserToDB] 📝 Creating customer with data:', JSON.stringify(newCustomer, null, 2));
        
        const createdUser = await lumi.entities.customers.create(newCustomer);
        console.log('[syncUserToDB] ✅ User created successfully! DB ID:', createdUser._id);
        
        // 🆕 發送歡迎郵件給新用戶（全局防重複檢查）
        if (!isEmailSent(userData.email)) {
          console.log('[syncUserToDB] 📧 ========== SENDING WELCOME EMAIL ==========');
          console.log('[syncUserToDB] 📧 Recipient:', userData.email);
          console.log('[syncUserToDB] 📧 Customer name:', userData.name || userData.email.split('@')[0]);
          console.log('[syncUserToDB] 📧 Registration date:', new Date().toISOString());
          
          markEmailSent(userData.email); // 標記為已發送（全局）
          
          try {
            const emailPayload = {
              customerEmail: userData.email,
              customerName: userData.name || userData.email.split('@')[0],
              registrationDate: new Date().toISOString()
            };
            
            console.log('[syncUserToDB] 📧 Calling sendWelcomeEmail API...');
            console.log('[syncUserToDB] 📧 Payload:', JSON.stringify(emailPayload, null, 2));
            
            // 获取当前用户的 access token
            const accessToken = lumi.auth.accessToken;
            console.log('[syncUserToDB] 📧 Access token available:', !!accessToken);
            
            const welcomeEmailResponse = await fetch('https://api.lumi.new/v1/functions/p373839333138763776/sendWelcomeEmail', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              },
              body: JSON.stringify(emailPayload)
            });
            
            console.log('[syncUserToDB] 📧 API Response status:', welcomeEmailResponse.status);
            console.log('[syncUserToDB] 📧 API Response statusText:', welcomeEmailResponse.statusText);
            
            const emailResult = await welcomeEmailResponse.json();
            console.log('[syncUserToDB] 📧 API Response body:', JSON.stringify(emailResult, null, 2));
            
            if (welcomeEmailResponse.ok) {
              console.log('[syncUserToDB] ✅ ========== WELCOME EMAIL SENT SUCCESSFULLY ==========');
              console.log('[syncUserToDB] ✅ Email ID:', emailResult.emailId);
            } else {
              console.error('[syncUserToDB] ❌ ========== FAILED TO SEND EMAIL ==========');
              console.error('[syncUserToDB] ❌ Status:', welcomeEmailResponse.status);
              console.error('[syncUserToDB] ❌ Error:', emailResult);
              // 失敗時移除全局標記，允許重試
              localStorage.removeItem(`email_sent_${userData.email}`);
            }
          } catch (emailError: any) {
            console.error('[syncUserToDB] ❌ ========== EMAIL EXCEPTION ==========');
            console.error('[syncUserToDB] ❌ Error type:', emailError.constructor?.name);
            console.error('[syncUserToDB] ❌ Error message:', emailError.message);
            console.error('[syncUserToDB] ❌ Error stack:', emailError.stack);
            // 失敗時移除全局標記，允許重試
            localStorage.removeItem(`email_sent_${userData.email}`);
          }
        } else {
          console.log('[syncUserToDB] ⏭️ Email already sent to this user (global check), skipping');
        }
      } else {
        console.log('[syncUserToDB] ℹ️ User already exists in customers collection');
        console.log('[syncUserToDB] ℹ️ Existing user email:', existingUsers[0]?.email);
        console.log('[syncUserToDB] ℹ️ Total existing users with this email:', existingUsers.length);
        console.log('[syncUserToDB] ⏭️ Skipping welcome email (user already registered)');
      }
    } catch (error) {
      console.error('[syncUserToDB] ❌ Failed to sync user to DB:', error);
    } finally {
      // 🔓 清理全局同步锁
      markUserSyncing(userData.email, false);
      console.log('[syncUserToDB] 🔓 Sync completed for user:', userData.email);
    }
  };

  // 🔥 立即檢查當前登入狀態（頁面載入時）
  // - 收藏數量改用 cache 初始化，避免首屏多次打 API
  useEffect(() => {
    console.log('[Header Mount] Checking current auth status...');
    const currentUser = lumi.auth.user;
    if (currentUser) {
      console.log('[Header Mount] User is already logged in:', currentUser.email);
    } else {
      console.log('[Header Mount] No user logged in');
    }
  }, []);

  // 🔥 監聽 Auth 變化（新登入事件）
  useEffect(() => {
    console.log('[Header] ========== Setting up onAuthChange listener ==========');
    const unsubscribe = lumi.auth.onAuthChange((authResult) => {
      console.log('[onAuthChange] ========== Auth state changed ==========');
      console.log('[onAuthChange] User:', authResult.user ? authResult.user.email : 'logged out');
      console.log('[onAuthChange] Full user data:', JSON.stringify(authResult.user, null, 2));
      
      setUser(authResult.user);
      if (authResult.user) {
        console.log('[onAuthChange] ✅ User logged in - triggering syncUserToDB');
        syncUserToDB(authResult.user);
        setIsLoginModalOpen(false); // Close modal on success
        // 收藏數量由 user?.email effect 觸發（內含去重/合併 + 429 fallback）
      } else {
        console.log('[onAuthChange] ℹ️ User logged out');
        setFavoriteCount(0);
      }
    });
    return () => {
      console.log('[Header] ========== Cleaning up onAuthChange listener ==========');
      unsubscribe();
    };
  }, [])

  // Fetch favorite count (集中請求 + 去重/合併 + cache + 429 fallback)
  const fetchFavoriteCount = async (userEmail: string) => {
    const { count } = await fetchFavoritesCountForUser(userEmail, {
      ttlMs: 30_000,
      cooldownMsOn429: 60_000,
    })
    setFavoriteCount(count)
  }


  // Load favorite count when user changes (with cache)
  useEffect(() => {
    const userEmail = user?.email
    if (!userEmail) {
      setFavoriteCount(0)
      return
    }

    // 統一入口：去重/合併 + 429 fallback 皆在 favoritesService 處理
    void fetchFavoriteCount(userEmail)
  }, [user?.email])

  // Listen for storage events (when favorites change)
  useEffect(() => {
    const handleFavoriteChange = () => {
      // 先用 cache 立即更新 UI，再視需要冷卻後再打 API
      setFavoriteCount(getFavoritesCountFromCache())
      if (user?.email) {
        void fetchFavoriteCount(user.email)
      }
    }
    window.addEventListener('favoriteChanged', handleFavoriteChange)
    return () => window.removeEventListener('favoriteChanged', handleFavoriteChange)
  }, [user?.email])
  
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  
  // 檢測是否為產品頁面 - 更新正則表達式以匹配新的路徑格式
  const isProductPage = () => {
    const pathPattern = /^\/(zh|en)\/shop\/[^\/]+\/[^\/]+\/[^\/]+$/
    return pathPattern.test(location.pathname)
  }
  
  // 關閉選單當路由改變時
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsUserMenuOpen(false)
    setActiveSubmenu(null)
  }, [location])

  // 防止背景滾動當選單開啟時
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])
  
  const navigation = {
    zh: [
      { name: '首頁', href: '/zh' },
      { 
        name: '線上購物', 
        href: '/zh/shop',
        submenu: [
          { name: '咖啡豆', href: '/zh/shop/beans' },
          { name: '濾掛咖啡', href: '/zh/shop/dripbag' },
          { name: '禮盒系列', href: '/zh/shop/gift' },
          { name: '器具設備', href: '/zh/shop/gear' }
        ]
      },
      { 
        name: '實體店面', 
        href: '/zh/store',
        submenu: [
          { name: '信義店', href: '/zh/store/xinyi' }
        ]
      },
      { 
        name: '醞玖製研中心', 
        href: '/zh/lab',
        submenu: [
          { name: '最新消息', href: '/zh/lab/news' },
          { name: '沖煮指南', href: '/zh/lab/guide' },
          { name: '豆子故事', href: '/zh/lab/beanstory' },
          { name: '活動資訊', href: '/zh/lab/events' }
        ]
      },
      { 
        name: 'B2B批發', 
        href: '/zh/wholesale'
      },
      { 
        name: '訂單查詢', 
        href: '/zh/order-tracking'
      }
    ],
    en: [
      { name: 'Home', href: '/en' },
      { 
        name: 'Shop', 
        href: '/en/shop',
        submenu: [
          { name: 'Coffee Beans', href: '/en/shop/beans' },
          { name: 'Drip Bags', href: '/en/shop/dripbag' },
          { name: 'Gift Sets', href: '/en/shop/gift' },
          { name: 'Equipment', href: '/en/shop/gear' }
        ]
      },
      { 
        name: 'Stores', 
        href: '/en/store',
        submenu: [
          { name: 'Xinyi Store', href: '/en/store/xinyi' }
        ]
      },
      { 
        name: 'Lab', 
        href: '/en/lab',
        submenu: [
          { name: 'News', href: '/en/lab/news' },
          { name: 'Guides', href: '/en/lab/guide' },
          { name: 'Bean Stories', href: '/en/lab/beanstory' },
          { name: 'Events', href: '/en/lab/events' }
        ]
      },
      { 
        name: 'Wholesale', 
        href: '/en/wholesale'
      },
      { 
        name: 'Order Tracking', 
        href: '/en/order-tracking'
      }
    ]
  }

  const toggleLanguage = () => {
    const newLang = currentLang === 'zh' ? 'en' : 'zh'
    // 移除當前語言前綴，保留剩餘路徑
    const currentPath = location.pathname.replace(/^\/(zh|en)/, '')
    // 如果當前在根路徑，直接跳轉到新語言根路徑
    const newPath = currentPath === '' || currentPath === '/' ? `/${newLang}` : `/${newLang}${currentPath}`
    navigate(newPath)
  }

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  }

  const handleLoginConfirm = async () => {
    try {
      await lumi.auth.signIn()
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  const handleLogout = () => {
    lumi.auth.signOut()
    setUser(null)
    setIsUserMenuOpen(false)
    setIsMobileMenuOpen(false)
    navigate(`/${currentLang}`)
  }

  return (
    <>
      {/* 🔒 第一排 - 品牌與功能區 (FIXED - 絕對固定) */}
      <div className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-50 backdrop-blur-md bg-opacity-95 dark:bg-opacity-95 transition-colors duration-300">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to={`/${currentLang}`} className="flex items-center space-x-3">
              <img 
                src="https://static.lumi.new/f4/f41cec5bb6b72110e177830b0f994a38.png"
                alt="ONGU STUDIO Logo"
                className="h-10 w-auto object-contain"
              />
              <div className="text-gray-800 dark:text-gray-200 hidden sm:block">
                <div className="text-lg font-light tracking-wider">ONGU STUDIO</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 -mt-1">醞玖製研</div>
              </div>
            </Link>

            {/* 桌面版功能區 (≥1024px) */}
            <div className="hidden lg:flex items-center space-x-6">
              {/* 主題切換 */}
              <ThemeToggle />
              
              {/* 語言切換 */}
              <button
                onClick={toggleLanguage}
                className="flex items-center space-x-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              >
                <Globe size={16} />
                <span className="text-sm font-light">{currentLang.toUpperCase()}</span>
              </button>

              {/* 心願清單 */}
              <Link
                to={`/${currentLang}/member/wishlist`}
                className="relative p-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors duration-200"
              >
                <Heart size={20} />
                {favoriteCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-black dark:bg-white text-white dark:text-black text-xs rounded-full flex items-center justify-center">
                    {favoriteCount > 99 ? '99+' : favoriteCount}
                  </span>
                )}
              </Link>

              {/* 購物車 */}
              <Link
                to={`/${currentLang}/cart`}
                className="relative p-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors duration-200"
              >
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-black dark:bg-white text-white dark:text-black text-xs rounded-full flex items-center justify-center">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </Link>

              {/* 登入/用戶區 - 最右邊 */}
              <div className="relative">
                {!user ? (
                  <button
                    onClick={handleLoginClick}
                    className="group flex items-center space-x-2 px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-sm hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all duration-300 tracking-widest"
                  >
                    <span className="text-xs font-medium tracking-widest uppercase">
                      {currentLang === 'zh' ? '登入會員' : 'Login Member'}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 overflow-hidden relative"
                  >
                    {user.picture ? (
                       <img src={user.picture} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                       <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300">
                          {(user.userName || 'U').charAt(0).toUpperCase()}
                       </div>
                    )}
                  </button>
                )}
                
                {/* 用戶下拉選單 */}
                {user && isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-3 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl py-2 z-[60] animate-fade-in-up origin-top-right">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 mb-1">
                       <p className="text-xs text-gray-500 dark:text-gray-400 font-light">Signed in as</p>
                       <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.email}</p>
                    </div>
                    
                    <Link
                      to={`/${currentLang}/member`}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-black dark:hover:text-white transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User size={16} className="mr-2" />
                      {currentLang === 'zh' ? '會員中心' : 'Member Center'}
                    </Link>
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
                    >
                      <LogOut size={16} className="mr-2" />
                      {currentLang === 'zh' ? '登出' : 'Logout'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 手機/平板版功能區 (<1024px) - 主題切換 + 購物車 + 漢堡按鈕 */}
            <div className="flex lg:hidden items-center space-x-3">
              {/* 主題切換 */}
              <ThemeToggle />
              
              {/* 購物車圖標 - 新增在行動版 */}
              <Link
                to={`/${currentLang}/cart`}
                className="relative p-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors duration-200"
              >
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-black dark:bg-white text-white dark:text-black text-xs rounded-full flex items-center justify-center">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </Link>
              
              {/* 漢堡按鈕 */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🌊 第二排 - 傳統導覽列（僅桌面版顯示；非產品頁面才顯示） */}
      {/*
        修正行為：原本用 sticky 會先被第一排 fixed header 壓住，滾動時才「被拉下來」出現。
        改為桌面版固定在 top-16，並加上占位 spacer，維持既有版面推擠與行動版隱藏邏輯。
      */}
      {!isProductPage() ? (
        <div className="hidden lg:block">
          <div className="fixed top-16 left-0 right-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 transition-colors duration-300 z-40 backdrop-blur-md bg-opacity-95 dark:bg-opacity-95">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
              <nav className="flex items-center space-x-8 h-12">
                {navigation[currentLang].map((item) => (
                  <div key={item.name} className="relative group">
                    <Link
                      to={item.href}
                      className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 text-sm font-light tracking-wide flex items-center py-2"
                    >
                      {item.name}
                      {item.submenu ? <ChevronDown size={14} className="ml-1" /> : null}
                    </Link>

                    {/* 桌面版下拉選單 */}
                    {item.submenu ? (
                      <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[60]">
                        <div className="py-2">
                          {item.submenu.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* 占位：保持原本導覽列在 document flow 的高度，避免內容上移 */}
          <div className="h-12" aria-hidden="true" />
        </div>
      ) : null}

      {/* 右側推拉式導覽面板 (<1024px) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30 
            }}
            className="fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-900 shadow-2xl z-[70] lg:hidden overflow-y-auto"
          >
            {/* 選單頭部 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://static.lumi.new/f4/f41cec5bb6b72110e177830b0f994a38.png"
                  alt="ONGU STUDIO Logo"
                  className="h-8 w-auto object-contain"
                />
                <div className="text-gray-800 dark:text-gray-200">
                  <div className="text-lg font-light tracking-wider">ONGU STUDIO</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 -mt-1">醞玖製研</div>
                </div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>

            {/* 選單內容 */}
            <div className="p-4 space-y-6">
              {/* 用戶區域 */}
              {user ? (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-sm p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-zinc-900 dark:bg-zinc-100 rounded-sm flex items-center justify-center text-white dark:text-black text-sm font-medium">
                      {user.picture ? (
                        <img src={user.picture} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        (user.userName || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <div className="text-gray-900 dark:text-gray-100 font-medium">{user.userName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {currentLang === 'zh' ? '會員' : 'Member'}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Link
                      to={`/${currentLang}/member`}
                      className="block text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {currentLang === 'zh' ? '會員中心' : 'Member Center'}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      {currentLang === 'zh' ? '登出' : 'Logout'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    handleLoginClick()
                    setIsMobileMenuOpen(false)
                  }}
                  className="w-full bg-black dark:bg-white text-white dark:text-black border border-black dark:border-white py-3 px-4 rounded-sm font-light tracking-widest flex items-center justify-center space-x-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all duration-300"
                >
                  <LogIn size={18} />
                  <span>{currentLang === 'zh' ? '登入 / 註冊' : 'Login / Register'}</span>
                </button>
              )}

              {/* 功能按鈕區 */}
              <div className="grid grid-cols-3 gap-3">
                {/* 語言切換 */}
                <button
                  onClick={() => {
                    toggleLanguage()
                    setIsMobileMenuOpen(false)
                  }}
                  className="flex flex-col items-center justify-center p-3 border border-gray-200 dark:border-gray-800 rounded-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
                >
                  <Globe size={20} className="text-gray-600 dark:text-gray-400 mb-1" />
                  <span className="text-xs text-gray-700 dark:text-gray-300">{currentLang.toUpperCase()}</span>
                </button>

                {/* 心願清單 */}
                <Link
                  to={`/${currentLang}/member/wishlist`}
                  className="flex flex-col items-center justify-center p-3 border border-gray-200 dark:border-gray-800 rounded-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 relative"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Heart size={20} className="text-gray-600 dark:text-gray-400 mb-1" />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {currentLang === 'zh' ? '心願' : 'Wishlist'}
                  </span>
                  {favoriteCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-black dark:bg-white text-white dark:text-black text-xs rounded-full flex items-center justify-center">
                      {favoriteCount > 99 ? '99+' : favoriteCount}
                    </span>
                  )}
                </Link>

                {/* 購物車 */}
                <Link
                  to={`/${currentLang}/cart`}
                  className="flex flex-col items-center justify-center p-3 border border-gray-200 dark:border-gray-800 rounded-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 relative"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <ShoppingCart size={20} className="text-gray-600 dark:text-gray-400 mb-1" />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {currentLang === 'zh' ? '購物車' : 'Cart'}
                  </span>
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-black dark:bg-white text-white dark:text-black text-xs rounded-full flex items-center justify-center">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </Link>
              </div>

              {/* 分隔線 */}
              <hr className="border-gray-200 dark:border-gray-700" />

              {/* 主要導覽選單 */}
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {currentLang === 'zh' ? '主要選單' : 'Main Menu'}
                </h3>
                
                {navigation[currentLang].map((item) => (
                  <div key={item.name}>
                    {/* 如果有子選單，點擊展開/收合 */}
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() => setActiveSubmenu(activeSubmenu === item.name ? null : item.name)}
                          className="w-full flex items-center justify-between p-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
                        >
                          <span className="font-medium">{item.name}</span>
                          <ChevronDown 
                            size={16} 
                            className={`transform transition-transform duration-200 ${
                              activeSubmenu === item.name ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        
                        {/* 子選單項目 */}
                        {activeSubmenu === item.name && (
                          <div className="ml-4 mt-1 space-y-1">
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className="block p-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className="flex items-center justify-between p-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="font-medium">{item.name}</span>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
        onLogin={handleLoginConfirm}
        currentLang={currentLang}
      />
    </>
  )
}

export default Header